import { get, uniqueId } from 'lodash'
import * as React from 'react'
import {
  DependencyState,
  useDependencies,
} from '../../organisms/workflow-template/useDependencies'
import SignatureCanvas from '../../atoms/signature-canvas'
import { AddressHistory } from '../../organisms/address-history'
import { AliasHistory } from '../../organisms/alias-history'
import {
  Attributes,
  OnboardingComponent,
  TemplateResponseData,
  ExtraData,
} from '../../organisms/workflow-template/types'
import { useValidation } from '../../organisms/workflow-template/useValidation'
import { SubmitOnboardingCallback } from '../../pages/onboarding-workflow/OnboardingWorkflow'
import { CheckboxComponent } from './CheckboxComponent'
import { DateComponent } from './DateComponent'
import ImageComponent from './ImageComponent'
import { ListCellComponent } from './ListCellComponent'
import { ListComponent } from './ListComponent'
import { PasswordComponent } from './PasswordComponent'
import { PlaceOfBirthComponent } from './PlaceOfBirthComponent'
import { RadioGroupComponent } from './radio-group/RadioGroupComponent'
import { SelectComponent } from './SelectComponent'
import { TextFieldComponent } from './TextFieldComponent'
import { VGSDocUploadComponent } from './VGSDocUploadComponent'
import { ProgressionListItemComponent } from './ProgressionListItemComponent'
import { RouteButtonComponent } from './RouteButtonComponent'
import { ProgressionListItemProps } from '../progression-list-item/ProgressionListItem'
import { TextComponent } from './ TextComponent'
import { FormPreviewComponent } from './FormPreviewComponent'
import { VGSSsnFieldComponent } from './VGSSsnFieldComponent'
import { USBackgroundCheckVGSFormCollectComponent } from './USBackgroundCheckVGSFormCollectComponent'
import { VGSFormComponent } from './VGSFormComponent'
import { LegalDocumentModalComponent } from './LegalDocumentModalComponent'
import { AnimatedImageComponent } from './AnimatedImageComponent'
import { TagComponent } from './TagComponent'
import { ButtonComponent } from './ButtonComponent'
import { USBackgroundCheckEditButton } from './USBackgroundCheckEditButton'
import { USLegalDocumentDisclosure } from './USLegalDocumentDisclosure'
import { NavigateButtonComponent } from './NavigateButtonComponent'
import { InlineTextComponent } from './InlineTextComponent'
import { StackComponent } from './StackComponent'
import { PromotionalCardsComponent } from './PromotionalCardsComponent'
import { InterpolateComponent } from './InterpolateComponent'
import { SvgComponent } from './SvgComponent'
import { IntrinsicElement } from './IntrinsicElement'
import { BankAccountFormComponent } from './BankAccountFormComponent'
import { RedirectComponent } from './RedirectComponent'

type ComponentKeys =
  | 'DATE_FIELD'
  | 'TEXT_FIELD'
  | 'DROP_DOWN_FIELD'
  | 'CHECK_BOX_FIELD'
  | 'ADDRESS_HISTORY'
  | 'PREVIOUS_ALIAS'
  | 'PASSWORD_FIELD'
  | 'IMAGE'
  | 'LIST_FIELD'
  | 'VGS_DOC_UPLOAD'
  | 'SIGNATURE_CAPTURE'
  | 'LIST_CELL'
  | 'TEXT'
  | 'PLACE_OF_BIRTH'
  | 'RADIO_GROUP'
  | 'PROGRESSION_LIST_ITEM'
  | 'ROUTE_BUTTON'
  | 'VGS_SSN_FIELD'
  | 'VGS_FORM_COMPONENT'
  | 'LEGAL_DOCUMENT_MODAL_COMPONENT'
  | 'FORM_PREVIEW_COMPONENT'
  | 'VGS_SSN_FIELD'
  | 'US_BACKGROUND_CHECK_VGS_FORM_COLLECT'
  | 'US_BACKGROUND_CHECK_EDIT_BUTTON'
  | 'ANIMATED_IMAGE_COMPONENT'
  | 'TAG_COMPONENT'
  | 'BUTTON_COMPONENT'
  | 'US_LEGAL_DOCUMENT_DISCLOSURE'
  | 'NAVIGATE_BUTTON'
  | 'INLINE_TEXT'
  | 'STACK'
  | 'PROMOTIONAL_CARDS'
  | 'INTERPOLATE'
  | 'SVG'
  | 'INTRINSIC_ELEMENT'
  | 'BANK_ACCOUNT_FORM'
  | 'REDIRECT'

const COMPONENT_MAP: {
  [key in ComponentKeys]: React.FC<OnboardingComponentProps>
} = {
  DATE_FIELD: DateComponent,
  TEXT_FIELD: TextFieldComponent,
  DROP_DOWN_FIELD: SelectComponent,
  CHECK_BOX_FIELD: CheckboxComponent,
  ADDRESS_HISTORY: AddressHistory,
  PREVIOUS_ALIAS: AliasHistory,
  PASSWORD_FIELD: PasswordComponent,
  IMAGE: ImageComponent,
  LIST_FIELD: ListComponent,
  VGS_DOC_UPLOAD: VGSDocUploadComponent,
  SIGNATURE_CAPTURE: SignatureCanvas,
  LIST_CELL: ListCellComponent,
  TEXT: TextComponent,
  PLACE_OF_BIRTH: PlaceOfBirthComponent,
  RADIO_GROUP: RadioGroupComponent,
  PROGRESSION_LIST_ITEM: ProgressionListItemComponent,
  ROUTE_BUTTON: RouteButtonComponent,
  VGS_SSN_FIELD: VGSSsnFieldComponent,
  VGS_FORM_COMPONENT: VGSFormComponent,
  LEGAL_DOCUMENT_MODAL_COMPONENT: LegalDocumentModalComponent,
  ANIMATED_IMAGE_COMPONENT: AnimatedImageComponent,
  TAG_COMPONENT: TagComponent,
  BUTTON_COMPONENT: ButtonComponent,
  FORM_PREVIEW_COMPONENT: FormPreviewComponent,
  US_BACKGROUND_CHECK_VGS_FORM_COLLECT: USBackgroundCheckVGSFormCollectComponent,
  US_BACKGROUND_CHECK_EDIT_BUTTON: USBackgroundCheckEditButton,
  US_LEGAL_DOCUMENT_DISCLOSURE: USLegalDocumentDisclosure,
  NAVIGATE_BUTTON: NavigateButtonComponent,
  INLINE_TEXT: InlineTextComponent,
  STACK: StackComponent,
  PROMOTIONAL_CARDS: PromotionalCardsComponent,
  INTERPOLATE: InterpolateComponent,
  SVG: SvgComponent,
  INTRINSIC_ELEMENT: IntrinsicElement,
  BANK_ACCOUNT_FORM: BankAccountFormComponent,
  REDIRECT: RedirectComponent,
}
export interface OnboardingComponentProps
  extends Attributes,
    Partial<DependencyState>,
    Partial<ProgressionListItemProps> {
  id: string
  onChange: (val: any) => void
  onSubmitStep: SubmitOnboardingCallback
  value: any
  error: string
  apiPath: string
  templateResponseData: TemplateResponseData
}

export const ComponentWrapper = (
  component: OnboardingComponent,
  onDataUpdate?: (path: string, value) => void,
  templateResponseData?: TemplateResponseData,
  onSubmitStep?: SubmitOnboardingCallback,
  extraData?: ExtraData
) => {
  const ComponentFromMap = COMPONENT_MAP[component.type]
  const { getError } = useValidation()

  const dependencies = useDependencies(
    templateResponseData,
    component.dependencies,
    extraData
  )

  if (ComponentFromMap) {
    const apiPath = component.be_mapping ? component.be_mapping.path : ''
    const value = get(templateResponseData, apiPath, '')
    // todo: this add creates a new function each render cycle, fully re-rendering the subsequent dom tree, there
    // should be a way to make it so react can be smart about this function
    const onChange = val => {
      onDataUpdate(apiPath, val)
      if (dependencies.clearOnChange && dependencies.clearOnChange.length > 0) {
        dependencies.clearOnChange.forEach(fieldApiPath => {
          onDataUpdate(fieldApiPath, '')
        })
      }
    }
    const error = getError(apiPath)
    const id = uniqueId('component_')
    const props: OnboardingComponentProps = {
      ...component.attributes,
      ...dependencies,
      id,
      onChange,
      onSubmitStep,
      value,
      error,
      apiPath,
      templateResponseData,
    }

    if (component.children) {
      props.children =
        typeof component.children === 'string'
          ? component.children
          : component.children.map(
              (childComponent: OnboardingComponent, index: number) => (
                <React.Fragment key={index}>
                  {ComponentWrapper(
                    childComponent,
                    onDataUpdate,
                    templateResponseData,
                    onSubmitStep,
                    extraData
                  )}
                </React.Fragment>
              )
            )
    }

    return !dependencies.isHidden && <ComponentFromMap {...props} />
  }
  console.warn(
    'Encountered a component which was not present in component_map',
    component.type
  )
  return null
}
